import { ChevronLeft, Loader2, Upload } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import FileUpload from '@/js/components/FileUpload';
import { fetchExistingDocuments } from '@/js/components/FileUploadBox';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import createApiClient from '@/api/axiosInstance';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const CreateContractScreen = ({
  setScreen,
  contract,
  setContract,
  setField,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiClient = createApiClient(navigate, dispatch);
  const token = useSelector((state) => state.authReducer.token);
  const user_type_id = useSelector(
    (state) => state?.authReducer?.userData?.user_type_id,
  );

  const [quotationData, setQuotationData] = useState({});
  const [comment, setComment] = useState('');

  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const getQuoteData = async () => {
      try {
        const response = await apiClient.post(
          '/api/quotation/get/quote-number',
          JSON.stringify({
            quote_number: contract?.quote_number,
          }),
        );

        const responseData = response?.data;
        setQuotationData(responseData.data);
      } catch (error) {
        console.error('ERROR IN GETTING QUOTE DATA');
      }
    };

    getQuoteData();
  }, []);

  // document upload
  const DOCUMENT_TITLES = {
    contractDoc: `Contract-${contract?.quote_number}`,
  };

  const [existingDocuments, setExistingDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [documentStates, setDocumentStates] = useState({
    contractDoc: null,
  });
  const [uploadProgress, setUploadProgress] = useState({
    contractDoc: 0,
  });

  const handleSend = async () => {
    setIsLoading(true);
    try {
      const docIDs = existingDocuments
        .filter(
          (document) =>
            document.document_name === `Contract-${contract?.quote_number}`,
        )
        .map((document) => document.id);

      const payload = {
        manufacturer_id: quotationData?.manufacturer_id,
        buyer_id: user_type_id,
        quotation_id: quotationData?.quotation_id,
        comment: comment,
        document_ids: docIDs,
      };

      const response = await apiClient.post(
        '/api/contract/create',
        JSON.stringify(payload),
      );

      setField('Contract');
      setScreen('ALL_PARTS');
    } catch (error) {
      console.error('ERROR IN SENDING CONTRACT');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="py-8 font-roboto">
      {/* Header */}
      <div className="flex gap-4 items-center mb-6">
        <ChevronLeft
          className="h-5 w-5 hover:cursor-pointer"
          onClick={() => {
            setField('Contract');
            setScreen('ALL_PARTS');
          }}
        />
        <h1 className="text-lg font-medium">
          Create Contract (Quotation #{contract.quote_number})
        </h1>
      </div>

      {/* Quote Info Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 text-sm text-gray-700">
        <div className="flex flex-col">
          <span className="text-gray-500">Quote Number</span>
          <span className="font-medium">Q#{contract.quote_number}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-gray-500">Part Name</span>
          <span className="font-medium">{contract.part_name}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-gray-500">Validity Date</span>
          <span className="font-medium">{contract.date}</span>
        </div>
      </div>

      {/* Upload Section */}
      <div className="flex flex-col gap-2 mb-8">
        <span className="text-sm text-gray-700">
          Upload your contract or agreement document to share with the
          manufacturer.
        </span>
        <span className="text-xs text-gray-500">
          Supported file types: PDF, DOC, Max size: 100MB per file
        </span>

        <div className="flex">
          <FileUpload
            DOCUMENT_TITLES={DOCUMENT_TITLES}
            MAX_FILE_SIZE={MAX_FILE_SIZE}
            ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
            existingDocuments={existingDocuments}
            setExistingDocuments={setExistingDocuments}
            documentStates={documentStates}
            setDocumentStates={setDocumentStates}
            uploadProgress={uploadProgress}
            setUploadProgress={setUploadProgress}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            setError={setError}
            key="contractDoc"
            id="contractDoc"
            label="Upload Contract"
            documentType={DOCUMENT_TITLES.contractDoc}
            existingDocument={existingDocuments.find(
              (doc) => doc.document_name === DOCUMENT_TITLES.contractDoc,
            )}
          />
        </div>
      </div>

      {/* Uploaded By Input */}
      <div className="flex flex-col gap-1 max-w-md">
        <label className="text-sm text-gray-700">Comment</label>
        <Textarea
          placeholder="Write a comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
        />
      </div>

      <div className="flex flex-col items-center mt-32">
        <Button
          variant="default"
          disabled={isLoading || existingDocuments.length === 0}
          className={`text-base text-white transition ease-in-out duration-300 ${
            isLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-darkPurple hover:bg-darkerPurple'
          } flex items-center justify-center disabled:bg-gray-200 disabled:text-gray-600`}
          onClick={() => {
            handleSend();
          }}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            'Send to Manufacturer'
          )}
        </Button>
      </div>
    </div>
  );
};

export default CreateContractScreen;
